import type { ComponentType } from "react";

// ─── Types ──────────────────────────────────────────────────────────────────
// Single source of truth mapping each route to its dynamic import(). Used by
// React.lazy() in routes.tsx AND by hover-prefetch in the sidebar, so the two
// can never drift out of sync — see JAA-857 architecture.md.

type DefaultExportModule = { default: ComponentType };
type DefaultExportImportFn = () => Promise<DefaultExportModule>;

// CustomDashboardRoutePages.tsx exports 3 named components from one file.
// routeImports holds one raw entry for the shared chunk; routes.tsx maps it
// to each named export individually via .then(m => ({ default: m.X })).
type CustomDashboardRoutePagesModule = () => Promise<
  typeof import("./components/custom-dashboard/CustomDashboardRoutePages")
>;

interface RouteImportMap {
  // ── Auth (guest-only) ───────────────────────────────────────────────────
  signIn: DefaultExportImportFn;
  forgotPassword: DefaultExportImportFn;
  resetPassword: DefaultExportImportFn;
  acceptInvite: DefaultExportImportFn;

  // ── Platform admin ──────────────────────────────────────────────────────
  adminDashboard: DefaultExportImportFn;
  manageAdmins: DefaultExportImportFn;
  addOrganization: DefaultExportImportFn;

  // ── Authenticated — facility summary / profile ──────────────────────────
  facilitySummary: DefaultExportImportFn;
  telemetryOutage: DefaultExportImportFn;
  profile: DefaultExportImportFn;

  // ── Facility scoped ──────────────────────────────────────────────────────
  facilityOverview: DefaultExportImportFn;
  aiInsights: DefaultExportImportFn;
  scenarioSimulator: DefaultExportImportFn;
  performanceAnalysis: DefaultExportImportFn;
  energyAnalysis: DefaultExportImportFn;
  systemStatus: DefaultExportImportFn;

  // ── Custom dashboards (feature-flagged) ─────────────────────────────────
  customDashboardsList: DefaultExportImportFn;
  customDashboardRoutePages: CustomDashboardRoutePagesModule;
}

// ─── Map ────────────────────────────────────────────────────────────────────

export const routeImports: RouteImportMap = {
  signIn: () => import("./components/auth/SignInPage"),
  forgotPassword: () => import("./components/auth/ForgotPasswordPage"),
  resetPassword: () => import("./components/auth/ResetPasswordPage"),
  acceptInvite: () => import("./components/auth/AcceptInvitePage"),
  adminDashboard: () => import("./components/admin-dashboard/AdminDashboard"),
  manageAdmins: () => import("./components/manage-admins/ManageAdminsPage"),
  addOrganization: () => import("./components/add-organization/AddOrganizationPage"),
  facilitySummary: () => import("./components/facility-summary/FacilitySummaryPage"),
  telemetryOutage: () => import("./components/telemetry-outage/TelemetryOutagePage"),
  profile: () => import("./components/my-profile/ProfilePage"),
  facilityOverview: () => import("./components/organizations/FacilityOverview"),
  aiInsights: () => import("./components/ai-insights/AiInsights"),
  scenarioSimulator: () => import("./components/scenario-simulator/ScenarioSimulator"),
  performanceAnalysis: () => import("./components/performance-analysis/PerformanceAnalysis"),
  energyAnalysis: () => import("./components/energy-analysis/EnergyAnalysis"),
  systemStatus: () => import("./components/system-status/SystemStatus"),
  customDashboardsList: () => import("./components/custom-dashboard/CustomDashboardsListPage"),
  customDashboardRoutePages: () => import("./components/custom-dashboard/CustomDashboardRoutePages"),
};
